import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { mdiChevronDown, mdiChevronUp, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import ActionContainer from 'components/ActionContainer/ActionContainer';
import Button from 'components/Button/Button';
import Loading from 'components/Loading/Loading';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FirestopDetailItem } from './FirestopDetailItem';

const useStyles = makeStyles((theme) => ({
  emptyState: {
    padding: theme.spacing(1, 0),
  },
}));

export const FirestopDetailsSection = ({
  loading,
  isLoadingFirestopDetails,
  open,
  setOpen,
  firestopDetails,
  onAddFirestopDetail,
  onChangeFirestopDetail,
  onDeleteFirestopDetail,
  renderFirestopDetailImage,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [openFirestopDetailsArray, setOpenFirestopDetailsArray] = useState([]);

  const activeFirestopDetails = useMemo(
    () => firestopDetails.filter((firestopDetail) => !firestopDetail.deletedAt),
    [firestopDetails]
  );

  useEffect(() => {
    setOpenFirestopDetailsArray((currentOpenFirestopDetailsArray) =>
      currentOpenFirestopDetailsArray.filter((id) =>
        activeFirestopDetails.some((firestopDetail) => firestopDetail.id === id)
      )
    );
  }, [activeFirestopDetails]);

  const handleAddFirestopDetail = () => {
    const createdFirestopDetailId = onAddFirestopDetail();

    if (!createdFirestopDetailId) {
      return;
    }

    setOpenFirestopDetailsArray((currentOpenFirestopDetailsArray) => [
      ...currentOpenFirestopDetailsArray,
      createdFirestopDetailId,
    ]);
  };

  return (
    <ActionContainer
      title={t('project_dialog.section_firestop_details')}
      closable
      open={open}
      setOpen={setOpen}
      color="darker"
    >
      {isLoadingFirestopDetails ? (
        <Grid item xs={12}>
          <Loading tiny />
        </Grid>
      ) : activeFirestopDetails.length > 0 ? (
        <>
          <Grid container item spacing={1} justifyContent="flex-end">
            <Grid item xs="auto">
              {openFirestopDetailsArray.length ? (
                <Button
                  color="primary"
                  outline
                  disabled={loading}
                  onClick={() => setOpenFirestopDetailsArray([])}
                >
                  {t('project_dialog.button_close_all')}
                  <Icon path={mdiChevronUp} size={1} />
                </Button>
              ) : (
                <Button
                  color="primary"
                  outline
                  disabled={loading}
                  onClick={() =>
                    setOpenFirestopDetailsArray(
                      activeFirestopDetails.map(
                        (firestopDetail) => firestopDetail.id
                      )
                    )
                  }
                >
                  {t('project_dialog.button_open_all')}
                  <Icon path={mdiChevronDown} size={1} />
                </Button>
              )}
            </Grid>
          </Grid>
          {activeFirestopDetails.map((firestopDetail) => (
            <Grid container item key={firestopDetail.id}>
              <FirestopDetailItem
                firestopDetail={firestopDetail}
                loading={loading || isLoadingFirestopDetails}
                openFirestopDetailsArray={openFirestopDetailsArray}
                setOpenFirestopDetailsArray={setOpenFirestopDetailsArray}
                onChangeFirestopDetail={onChangeFirestopDetail}
                onDeleteFirestopDetail={onDeleteFirestopDetail}
                renderFirestopDetailImage={renderFirestopDetailImage}
              />
            </Grid>
          ))}
        </>
      ) : (
        <Grid item xs={12}>
          <Typography className={classes.emptyState}>
            {t('common.no_options')}
          </Typography>
        </Grid>
      )}
      <Grid item xs={12} style={{ marginTop: 8 }}>
        <Button
          color="primary"
          fullWidth
          disabled={loading || isLoadingFirestopDetails}
          onClick={handleAddFirestopDetail}
        >
          <Icon path={mdiPlus} size={1} />
          {t('project_dialog.button_add_new')}
        </Button>
      </Grid>
    </ActionContainer>
  );
};

FirestopDetailsSection.propTypes = {
  loading: PropTypes.bool,
  isLoadingFirestopDetails: PropTypes.bool,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  firestopDetails: PropTypes.array,
  onAddFirestopDetail: PropTypes.func,
  onChangeFirestopDetail: PropTypes.func,
  onDeleteFirestopDetail: PropTypes.func,
  renderFirestopDetailImage: PropTypes.func,
};

FirestopDetailsSection.defaultProps = {
  loading: false,
  isLoadingFirestopDetails: false,
  open: false,
  setOpen: () => {},
  firestopDetails: [],
  onAddFirestopDetail: () => {},
  onChangeFirestopDetail: () => {},
  onDeleteFirestopDetail: () => {},
  renderFirestopDetailImage: null,
};
